import { Box, Flex, Icon } from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { HiOutlineSparkles } from "react-icons/hi2";

import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import { CATEGORIES, useSendGAEvent } from "../../../utils/googleAnalytics";
import {
  hideIntercomButton,
  showIntercomButton,
} from "../../../utils/intercom";
import { CallFragment } from "../../graphql";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import useCurrentUser from "../../hooks/useCurrentUser";
import { ClipRange } from "../../types";
import { AskInputTrigger } from "../Ask/AskInputTrigger";
import {
  AskPanel,
  introMessageForInterviews,
  suggestedInterviewMessages,
} from "../Candidate/Ask/AskPanel";
import { MediaPlayerInterface } from "../MediaPlayer/useMediaPlayer";
import {
  ClipsTab,
  NotesV2Tab,
  SidebarTab,
  SidebarTabs,
  TrackersTab,
  TrainingTab,
  Transcript,
} from "../Recording";
import AiTab from "../Recording/SidebarTabs/Ai/AiTab";
import { useAsk2Enabled } from "../Recording/SidebarTabs/Ai/Ask/useAsk2Enabled";
import NotesV3Tab from "../Recording/SidebarTabs/NotesV3Tab";
import ScoresTab from "../Recording/SidebarTabs/ScoresTab";
import { useCallSidebar } from "./useCallSidebar";
import { CallTab } from "./utils";

type SidebarBounds = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type CallSidebarProps = {
  call: Pick<
    CallFragment,
    | "id"
    | "greenhouseScorecardLinkWithId"
    | "ashbyFeedbackLinkWithId"
    | "leverInterviewLinkWithId"
    | "duration"
    | "interviewerIds"
    | "canEdit"
    | "canShare"
    | "canShareExternal"
    | "clipsCount"
    | "position"
    | "speakers"
    | "transcriptLanguageCode"
    | "aiNotesCustomFormat"
    | "aiNotesSupportedFormats"
    | "scoringEnabled"
    | "iaVersion"
  >;
  currentTab?: CallTab;
  player: MediaPlayerInterface;
  onTabChange?(t: CallTab): void;
  onSeek(t: number): void;
  onCreateClipClick?(range?: ClipRange): void;
  onCopyTranscript?(): void;
  autoScrollEnabled: boolean;
  setAutoScrollEnabled(enabled: boolean): void;
  isVideoVisible?: boolean;
  disableAutofill?: boolean;
  notesForCurrentUserOnly: boolean;
  hideVideo(): void;
  focusVideo(): void;
  isVideoCall?: boolean;
};

const CallSidebar: React.FC<CallSidebarProps> = ({
  call,
  currentTab,
  player,
  onTabChange,
  onSeek,
  onCreateClipClick,
  onCopyTranscript,
  autoScrollEnabled,
  setAutoScrollEnabled,
  isVideoCall,
  isVideoVisible,
  disableAutofill,
  notesForCurrentUserOnly,
  hideVideo,
  focusVideo,
}) => {
  const currentUser = useCurrentUser();
  const { allowAiNotes } = currentUser.organization;
  const isSmallScreen = useIsSmallScreen();
  const [userHasChangedTab, setUserHasChangedTab] = useState(false);
  const [askExpanded, setAskExpanded] = useState(false);
  const sendGAEvent = useSendGAEvent();
  const poisDisabled = useFeatureFlag("sidebar-pois:disabled");
  const ask2Enabled = useAsk2Enabled();
  const hasLabeledCandidate = !!call.speakers.find((s) => s.isCandidate);
  const canShowAiNotesFeature = allowAiNotes;

  // Track sidebar bounds for fixed-position Ask panel on desktop
  // Uses ResizeObserver to detect layout changes (e.g., call-in-progress banner)
  const containerRef = useRef<HTMLDivElement>(null);
  const [sidebarBounds, setSidebarBounds] = useState<SidebarBounds | null>(
    null
  );

  const updateBounds = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setSidebarBounds({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
  }, []);

  // Update bounds when Ask panel is open on desktop
  // ResizeObserver detects layout shifts (e.g., call-in-progress banner appearing)
  useEffect(() => {
    if (!askExpanded || isSmallScreen) return;

    updateBounds();

    const resizeObserver = new ResizeObserver(updateBounds);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also listen for window resize and scroll (for layout shifts)
    window.addEventListener("resize", updateBounds);
    window.addEventListener("scroll", updateBounds, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateBounds);
      window.removeEventListener("scroll", updateBounds, true);
    };
  }, [askExpanded, isSmallScreen, updateBounds]);

  /**
   * Assembles props for various page components
   */
  const {
    trackers,
    clips,
    training,
    notesV2: notes,
    notesV3,
    transcript,
    scorecard,
  } = useCallSidebar({
    call,
    currentTab,
    disableAutofill,
    notesForCurrentUserOnly,
    onCallNotesV2QueryCompleted: (data) => {
      if (
        !userHasChangedTab &&
        !data.call?.generalNotes.length &&
        !data.call?.questions.some((q) => q.questionNotes.length > 0)
      ) {
        if (canShowAiNotesFeature) {
          onTabChange?.(CallTab.AI_NOTES);
        } else {
          onTabChange?.(CallTab.POIS);
        }
      }
    },
    onCallNotesV3QueryCompleted: (data) => {
      if (
        !userHasChangedTab &&
        !data.call?.generalNotes.length &&
        (!data.call?.notesScorecards ||
          !data.call?.notesScorecards
            .flatMap((scorecard) => scorecard.items)
            .some(
              (item) =>
                item.questionNotes.length > 0 ||
                item.childItems.some(
                  (childItem) => childItem.questionNotes.length > 0
                )
            ))
      ) {
        if (canShowAiNotesFeature) {
          onTabChange?.(CallTab.AI_NOTES);
        } else {
          onTabChange?.(CallTab.POIS);
        }
      }
    },
  });

  /**
   * Navigate to training tab if training is present
   */
  useEffect(() => {
    if (training.callTraining) {
      onTabChange?.(CallTab.TRAINING);
    }
  }, [training.callTraining]);

  /**
   * Go to transcript tab on small screens, fallback to notes
   * tab if going from small screen to large screen
   */
  useEffect(() => {
    if (isSmallScreen) {
      onTabChange?.(CallTab.NOTES);
    } else if (currentTab === CallTab.TRANSCRIPT) {
      onTabChange?.(CallTab.NOTES);
    }
  }, [isSmallScreen]);

  /**
   * Navigate to Scores tab if scoring is enabled and current user hasn't finished filling out their scorecard
   */
  useEffect(() => {
    if (!scorecard.isCurrentUserScorecardComplete) {
      onTabChange?.(CallTab.SCORES);
    }
  }, [scorecard.isCurrentUserScorecardComplete]);

  const intercomEnabled = useFeatureFlag("interview-page-intercom");
  useEffect(() => {
    const intercomTabs = [
      CallTab.NOTES,
      CallTab.AI_NOTES,
      CallTab.POIS,
      CallTab.CLIPS,
    ];

    if (intercomEnabled && intercomTabs.includes(currentTab as CallTab)) {
      showIntercomButton();
    } else {
      hideIntercomButton();
    }
  }, [intercomEnabled, currentTab]);
  /** restore intercom button on teardown */
  useEffect(() => () => showIntercomButton(), []);

  // Tab will be auto-selected to something else, wait a moment
  // before rendering the tab to avoid a flash of the wrong tab
  const generalNotes =
    call.iaVersion === 3.0 ? notesV3.generalNotes : notes.generalNotes;

  const hasQuestionNotes =
    call.iaVersion !== 3.0
      ? notes.questions.some((q) => q.questionNotes.length > 0)
      : notesV3.scorecardItems.some(
          (item) =>
            item.questionNotes.length > 0 ||
            item.childItems.some(
              (childItem) => childItem.questionNotes.length > 0
            )
        );

  if (
    !userHasChangedTab &&
    CallTab.NOTES === currentTab &&
    generalNotes.length === 0 &&
    !hasQuestionNotes
  ) {
    return null;
  }

  const { isCurrentUserScorecardComplete, ...scorecardTabProps } = scorecard;

  const onAskOpen = ask2Enabled
    ? () => {
        sendGAEvent("ask_open", "call_review" as CATEGORIES);
        setAskExpanded(true);
      }
    : undefined;

  return (
    <Box ref={containerRef} position="relative" height="100%">
      <SidebarTabs
        id="call-sidebar"
        currentTab={currentTab}
        onTabChange={(newTab) => {
          if (newTab !== currentTab) {
            setUserHasChangedTab(true);
            sendGAEvent("sidebar_tab_change", "call_review", newTab);
          }
          onTabChange?.(newTab);
        }}
      >
        <SidebarTab id={CallTab.NOTES} displayName="Notes">
          <Flex direction="column" minH="100%">
            {call.iaVersion === 3.0 ? (
              <NotesV3Tab
                {...notesV3}
                call={call}
                callId={call.id}
                callDuration={call.duration as number}
                interviewerIds={call.interviewerIds}
                notesReadOnly={!call.canEdit}
                onClickTimestamp={onSeek}
                setClipRange={isSmallScreen ? undefined : onCreateClipClick}
                playerTime={player.time}
                overflow="hidden"
              />
            ) : (
              <NotesV2Tab
                {...notes}
                callId={call.id}
                callDuration={call.duration as number}
                interviewerIds={call.interviewerIds}
                notesReadOnly={!call.canEdit}
                onClickTimestamp={onSeek}
                setClipRange={isSmallScreen ? undefined : onCreateClipClick}
                playerTime={player.time}
                overflow="hidden"
              />
            )}
            {onAskOpen && (
              <AskInputTrigger
                onClick={onAskOpen}
                mt="auto"
                position="sticky"
                bottom={0}
              />
            )}
          </Flex>
        </SidebarTab>

        {canShowAiNotesFeature && (
          <SidebarTab
            id={CallTab.AI_NOTES}
            displayName={
              <Flex
                alignItems="center"
                data-tour-id="sidebar-tab-ai-notes"
                textColor="purple.600"
              >
                <Icon
                  as={HiOutlineSparkles}
                  color="purple.600"
                  h="16px"
                  mr="3px"
                  w="16px"
                />
                AI
              </Flex>
            }
          >
            <AiTab
              call={call}
              hasLabeledCandidate={hasLabeledCandidate}
              onClickTimestamp={onSeek}
              onAskOpen={onAskOpen}
            />
          </SidebarTab>
        )}

        {!poisDisabled && (
          <SidebarTab id={CallTab.POIS} displayName="POIs">
            <Flex direction="column" minH="100%">
              <TrackersTab
                onSeek={onSeek}
                {...trackers}
                transcriptLanguageCode={call.transcriptLanguageCode}
                doesCallHaveTranscript={transcript.data.transcript.length > 0}
              />
              {onAskOpen && (
                <AskInputTrigger
                  onClick={onAskOpen}
                  mt="auto"
                  position="sticky"
                  bottom={0}
                />
              )}
            </Flex>
          </SidebarTab>
        )}

        {isSmallScreen && (
          <SidebarTab id={CallTab.TRANSCRIPT} displayName="Transcript">
            <Transcript
              h="100%"
              callId={call.id}
              speakers={call.speakers}
              player={player}
              autoScrollEnabled={autoScrollEnabled}
              setAutoScrollEnabled={setAutoScrollEnabled}
              isVideoCall={isVideoCall}
              isVideoVisible={isVideoVisible}
              hideVideo={hideVideo}
              focusVideo={focusVideo}
              onCopyTranscript={onCopyTranscript}
              onAskOpen={onAskOpen}
              {...transcript.data}
            />
          </SidebarTab>
        )}

        {!isSmallScreen && onCreateClipClick && (
          <SidebarTab
            id={CallTab.CLIPS}
            displayName={`Clips${
              call.clipsCount > 0 ? ` (${call.clipsCount})` : ""
            }`}
          >
            <ClipsTab
              callId={call.id}
              positionId={call.position?.id}
              readOnly={!call.canEdit}
              onCreateClipClick={onCreateClipClick}
              canShare={call.canShare || call.canShareExternal}
              {...clips}
              onAskOpen={onAskOpen}
            />
          </SidebarTab>
        )}

        {training.callTraining && !isSmallScreen && (
          <SidebarTab id={CallTab.TRAINING} displayName="Training">
            <TrainingTab
              {...training}
              callTraining={training.callTraining}
              pt="6"
              h="100%"
            />
          </SidebarTab>
        )}

        {call.scoringEnabled && (
          <SidebarTab id={CallTab.SCORES} displayName="Scores">
            <ScoresTab {...scorecardTabProps} call={call} h="100%" />
          </SidebarTab>
        )}
      </SidebarTabs>

      {/* Ask Assistant slide-up overlay - uses fixed positioning for reliable animation */}
      <Box
        position="fixed"
        // On mobile: full viewport. On desktop: constrained to sidebar bounds.
        top={isSmallScreen ? 0 : sidebarBounds?.top ?? 0}
        left={isSmallScreen ? 0 : sidebarBounds?.left ?? 0}
        width={isSmallScreen ? "100vw" : sidebarBounds?.width ?? "100%"}
        height={isSmallScreen ? "100vh" : sidebarBounds?.height ?? "100%"}
        bg="white"
        transform={askExpanded ? "translateY(0)" : "translateY(100%)"}
        transition="transform 0.25s ease-out"
        willChange="transform"
        zIndex={10}
        visibility={askExpanded ? "visible" : "hidden"}
        sx={{
          transitionProperty: "transform, visibility",
          transitionDuration: "0.25s, 0s",
          transitionDelay: askExpanded ? "0s, 0s" : "0s, 0.25s",
        }}
      >
        <AskPanel
          requestPath={`/interview/${call.id}/ask2`}
          onClickTimestamp={onSeek}
          onClose={() => {
            sendGAEvent("ask_close", "call_review" as CATEGORIES);
            setAskExpanded(false);
          }}
          hidden={!askExpanded}
          suggestedMessages={suggestedInterviewMessages}
          introMessage={introMessageForInterviews}
          eventCategory={"call_review" as CATEGORIES}
        />
      </Box>
    </Box>
  );
};

export default CallSidebar;
